<template>
  <div class="collection-grid__container">
    <div class="container">
      <div class="collection-grid__layout">
        <collection-sidebar-b
          v-if="notMobileTablet && visibilitySettings.sidebarDisabled !== true"
          :default-sort="defaultSort"
          :filters-disabled="visibilitySettings.filtersDisabled"
          :tags="tags"
          :filters="filters"
        ></collection-sidebar-b>
        <div
          class="collection-grid__wrapper collection-grid__wrapper--sidebar"
          :class="[
            visibilitySettings.filtersDisabled === 1 &&
              'collection-grid__wrapper--filters-disabled'
          ]"
        >
          <collection-grid-header-b
            :pagination-amount="paginationAmount"
            :total-pages="totalPages"
            :tags="tags"
            :filters="filters"
            :default-sort="defaultSort"
            :filtersDisabled="visibilitySettings.filtersDisabled"
          ></collection-grid-header-b>
          <collection-active-filters-b></collection-active-filters-b>

          <fade-transition
            key="grid"
            class="collection-grid no-promo-tile"
            v-if="this.fetchingStatus === false && this.currentTotal >= 1"
            :duration="{ enter: 800, leave: 200 }"
            group
          >
            <div
              class="collection-grid-item"
              v-for="(product, index) in currentProducts"
              :key="`nopromo_${product.id}`"
            >
              <product-tile
                :selected-variant="getCurrentFilteredVariant(product)"
                :showWishlist="wishlistEnabled"
                :show-mobile-ATC="true"
                :collection-handle="currentCollectionHandle"
                :collection-gender="collectionGender"
                :key="product.id"
                :product="product"
                :onAddToCart="addToCart"
                :style="`order: ${index}`"
                collection-template="b"
              ></product-tile>
            </div>
          </fade-transition>

          <fade-transition :duration="{ enter: 600, leave: 200 }">
            <div
              class="collection-grid collection-grid__skeleton"
              v-if="this.fetchingStatus === true"
              key="skeleton"
            >
              <div
                class="collection-grid__skeleton-tile"
                v-for="n in paginationAmount"
                :key="`skel-${n}`"
              >
                <product-tile-skeleton></product-tile-skeleton>
              </div>
            </div>
          </fade-transition>
          <fade-transition :duration="{ enter: 600, leave: 200 }">
            <div
              class="collection-grid__no-results"
              v-show="this.fetchingStatus === false && this.currentTotal === 0"
              key="noProducts"
            >
              <h3 class="h7">Bummer.</h3>
              <p>
                We couldn’t find any products using your currently selected
                filters.
              </p>
            </div>
          </fade-transition>
          <collection-grid-footer-b>
            <collection-pagination-controls-b
              v-show="this.fetchingStatus === false && this.currentTotal > 0"
              :init-pages="totalPages"
              :pagination-amount="paginationAmount"
              :bottom-pagination="true"
            ></collection-pagination-controls-b>
          </collection-grid-footer-b>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { FadeTransition } from 'vue2-transitions'

import { handleize, unhandleize } from '@/scripts/filters/string.js'
import { getCollectionCacheState } from '@/scripts/helpers/collections';

// import CollectionSections from './CollectionSections.vue'
import CollectionGridHeaderB from './CollectionGridHeaderB.vue'
import CollectionGridFooterB from './CollectionGridFooterB.vue'
import CollectionTotalResults from './CollectionTotalResults.vue'
import CollectionPaginationControlsB from './CollectionPaginationControlsB.vue'
import CollectionSidebarB from './CollectionSidebarB.vue'
// import CollectionPromoTile from './CollectionPromoTile.vue'
import CollectionActiveFiltersB from './CollectionActiveFiltersB.vue'
import CtaTile from './CtaTile.vue'
import ProductTile from '@/scripts/components/product/ProductTile.vue'
import ProductTileSkeleton from '@/scripts/components/product/ProductTileSkeleton.vue'
import SelectInput from '@/scripts/components/forms/SelectInput.vue'
import VButton from '@/scripts/components/buttons/VButton.vue'

import { sortProductsBy } from '@/scripts/helpers/sort.js'

export default {
  name: 'CollectionGridB',
  components: {
    CtaTile,
    ProductTile,
    ProductTileSkeleton,
    SelectInput,
    CollectionSidebarB,
    CollectionGridHeaderB,
    CollectionGridFooterB,
    CollectionPaginationControlsB,
    CollectionTotalResults,
    CollectionActiveFiltersB,
    FadeTransition,
    VButton
    // CollectionPromoTile,
    // CollectionSections
  },
  props: {
    wishlistEnabled: Boolean,
    handle: String,
    initCollectionData: Object,
    initCollectionTotal: Number,
    initCollectionProducts: Array,
    totalProducts: Number,
    paginationAmount: Number,
    collectionGender: String,
    tags: Array,
    filters: Array,
    visibilitySettings: {
      type: Object,
      default: () => ({
        filtersDisabled: Number
      })
    }
  },
  computed: {
    ...mapState({
      fetchingStatus: state => state.collections.fetchingStatus,
      currentHandle: state => state.collections.currentHandle,
      currentPage: state => state.collections.currentPage,
      currentSort: state => state.collections.currentSort,
      currentFilters: state => state.collections.currentFilters,
      currentProducts: state => state.collections.currentProducts,
      currentTotal: state => state.collections.currentTotal,
    }),
    totalPages () {
      return Math.ceil(this.currentTotal / this.paginationAmount)
    },
    notMobileTablet () {
      return this.$mq !== 'mobile' && this.$mq !== 'tablet'
    },
    firstRelevantFilter () {
      const relevant = this.currentFilters.find(
        filter => filter.includes('color') || filter.includes('pattern')
      )
      if (!relevant) {
        return ''
      }
      if (relevant.startsWith('color-')) {
        return relevant.split('color-')[1]
      }
      if (relevant.startsWith('pattern-')) {
        return relevant.split('pattern-')[1]
      }
    },
    defaultSort () {
      const data = this.initCollectionData
      return data && data.sort_order ? data.sort_order : 'best-selling'
    },
    currentCollectionHandle () {
      const data = this.initCollectionData
      return data && data.handle ? data.handle : 'all'
    }
  },
  methods: {
    addToCart (id) {
      const addProduct = {
        id: id,
        quantity: 1
      }
      this.$store
        .dispatch('cart/addItems', addProduct)
        .then(() => this.$store.dispatch('cart/afterAddToCart'))
        .catch(error =>
          this.$store.dispatch('toast/send', { text: error, type: 'error' })
        )
        .then(() => (this.adding = false))
    },
    getCurrentFilteredVariant (currentProduct) {
      if (this.firstRelevantFilter !== '') {
        const filteredVariant = currentProduct.variants.find(variant => {
          return variant.options.find(
            option => handleize(option) === this.firstRelevantFilter
          )
        })
        return filteredVariant || currentProduct.variants[0]
      }
      return currentProduct.variants[0]
    }
  },
  created () {
    // Load from session cache if available
    let cacheState = getCollectionCacheState(this.currentCollectionHandle);
    if (!!cacheState) return this.$store.dispatch('collections/setCollectionStateFromCache', cacheState);

    // Load a fresh dataset
    this.$store.dispatch('collections/setInitialCollectionState', {
      handle: this.currentCollectionHandle,
      products: this.initCollectionProducts,
      total: this.initCollectionTotal,
      sort: this.defaultSort
    })

    const params = new URL(document.location).searchParams
    let filters = []

    for (const [key, value] of params.entries()) {
      if (key === 'filters' && typeof value === 'string') {
        filters = value.split(',')
      }
    }

    this.$store.dispatch('collections/updateFilters', filters)
  },
}
</script>

<style scoped lang="scss">
.container {
  padding: 0 $grid-gutter;
  @include mobile-only {
    padding: 0;
  }
}

.collection-grid {
  position: relative;
  text-align: left;
  font-size: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: stretch;

  @include mobile-tablet {
    margin: 0 -3px;
  }

  &.justify-start {
    /deep/ .collection-promo-tile {
      display: none;
    }
  }

  &__skeleton {
    min-height: 100vh;
  }

  &__layout {
    display: flex;
  }

  &__wrapper {
    width: 100%;
    &--filters-disabled {
      max-width: 1127px;
      margin: 0 auto;
    }
    @include mobile-only {
      max-width: 344px;
      margin: 0 auto;
    }
  }

  &__header-pagination-controls {
    position: absolute;
    top: 4px;
    left: 0;
    display: flex;
  }

  &__filters {
    position: absolute;
    top: 4px;
    right: 0;
    border: none;
  }

  &__sort {
    &-wrapper {
      display: flex;
      justify-content: center;
      margin-bottom: 18px;

      @include tablet-up {
        justify-content: flex-end;
        margin-bottom: 36px;
      }
    }
  }

  &__loading {
    position: absolute;
    width: 100%;
    display: flex;
    justify-content: center;
  }
}

.collection-grid-item {
  display: flex;
}

.collection-grid__wrapper--no-sidebar .collection-grid > * {
  margin-right: $collection-tile-side-margin-mobile;
  margin-bottom: 24px;
  width: 100%;
  display: inline-block;
  vertical-align: top;

  @include mobile-only {
    width: calc(50%);
    margin-right: 0;
    margin-bottom: 0;
  }

  @include tablet-only {
    width: calc(33%);
    margin-right: 0;
    margin-bottom: 0;
  }

  @include desktop-up {
    width: calc(25% - #{($collection-tile-side-margin-tablet * 3) / 4});
    margin-right: $collection-tile-side-margin-tablet;
    margin-bottom: 48px;

    &:nth-child(4n) {
      margin-right: 0;
    }
  }
}

.collection-grid__wrapper--sidebar .collection-grid > * {
  margin-right: $collection-tile-side-margin-mobile;
  margin-bottom: 12px;
  width: 100%;
  display: inline-block;
  vertical-align: top;

  @include mobile-only {
    width: calc(50%);
    margin-right: 0;
    margin-bottom: 29px;
    padding: 3px;
  }

  @include tablet-only {
    width: calc(33%);
    margin-right: 0;
    margin-bottom: 29px;
    padding: 3px;
  }

  @include desktop-up {
    width: calc(33.3333% - 12px);
    margin-right: $collection-tile-side-margin-tablet;
    margin-bottom: 40px;

    &:nth-child(3n) {
      margin-right: 0;
    }
  }
}

.grid-sort-animation-move {
  transition: transform 0.5s cubic-bezier(0.77, 0, 0.175, 1);
}

.collection-grid__no-results {
  padding: 30px;
  padding-top: 0;

  @include tablet-up {
    padding: 0;
    padding-top: 132px;
    padding-bottom: 30px;
  }
}
</style>
